'use client'

import { useMutation, useQuery } from '@tanstack/react-query'

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  toast,
} from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import { getInitials } from '@helper/utils/format'
import cn from '@config/tailwind/cn'

import type { Student } from '../types'
import { useSharedTRPC } from '../../../context'
import { useStudentMutations } from '../use-student-mutations'

interface GivePremiumDialogProps {
  student: Student
  onClose: () => void
}

/**
 * Hands one of the school's unused premium seats to a student.
 *
 * Seats are pooled per membership, so the count shown here is the school's whole remaining stock —
 * spending one on this student is what the rest of the roster loses.
 */
export function GivePremiumDialog({ student, onClose }: GivePremiumDialogProps) {
  const trpc = useSharedTRPC()
  const { invalidate } = useStudentMutations()

  // Owned, unassigned seats across every pool — the same read and the same filter the row menu
  // counts from, so the dialog and the menu that opened it can never report different numbers.
  const { data: premiums } = useQuery(trpc.myOrganization.getActiveSeats.queryOptions())
  const availableSeats = (premiums ?? []).filter((premium) => premium.childId === null).length

  const givePremium = useMutation(trpc.myOrganization.giveChildPremium.mutationOptions())

  const handleConfirm = async () => {
    try {
      await givePremium.mutateAsync({ userId: student.id })
      await invalidate()
      toast.success(`${student.name} sekarang punya akses premium.`)
      onClose()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Gagal memberikan kursi premium')
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent className='srd:w-full srd:max-w-md'>
        <DialogHeader>
          <DialogTitle>Berikan kursi premium ke murid ini?</DialogTitle>
          <DialogDescription>
            Satu kursi premium sekolah akan dipakai, dan murid ini langsung dapat akses ke semua buku dan pelajaran.
          </DialogDescription>
        </DialogHeader>

        <div className='srd:flex srd:flex-nowrap srd:items-center srd:gap-x-2'>
          <Avatar profile='user'>
            {student.image && <AvatarImage src={student.image} alt={student.name} />}
            <AvatarFallback>{getInitials(student.name)}</AvatarFallback>
          </Avatar>
          <span className={cn(typographyVariants({ variant: 'label-md' }), 'srd:text-neutral-black')}>
            {student.name}
          </span>
        </div>

        <div className='srd:bg-grey-10 srd:flex srd:items-center srd:justify-between srd:rounded-xl srd:p-3'>
          <span className={cn(typographyVariants({ variant: 'body-md' }), 'srd:text-grey-100')}>Kursi tersedia</span>
          <span className={cn(typographyVariants({ variant: 'title-sm' }), 'srd:text-neutral-black')}>
            {availableSeats}
          </span>
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={onClose} disabled={givePremium.isPending}>
            Batal
          </Button>
          <Button
            onClick={() => void handleConfirm()}
            loading={givePremium.isPending}
            disabled={givePremium.isPending || availableSeats === 0}
          >
            Ya, berikan kursi
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
